<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-loading-bay-efficiency">
    <nx-report-header title="装车台效率排名表">
      <div class="kb-header-actions">
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <el-form :inline="true" class="kb-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="时间范围">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始"
          end-placeholder="结束"
          value-format="yyyy-MM-dd"
          :picker-options="pickerOptions"
          style="width: 260px"
          @change="loadData"
        />
      </el-form-item>
      <el-form-item label="排名依据">
        <el-select v-model="rankMetric" style="width: 200px" @change="onRankMetricChange">
          <el-option label="综合效率分" value="score" />
          <el-option label="装车车数" value="trips" />
          <el-option label="平均单均装车时长" value="avgMin" />
          <el-option label="设备利用率" value="util" />
        </el-select>
      </el-form-item>
    </el-form>

    <el-empty
      v-if="loaded && !data.bay1Found && !data.bay2Found"
      description="暂无装车台数据"
    />

    <template v-else-if="loaded">
      <div class="kb-kpi-row">
        <div class="kb-kpi-card">
          <div class="kb-kpi-label">总装车次数（两线合计）</div>
          <div class="kb-kpi-value">{{ fmtInt(summary.totalTripCount) }}</div>
        </div>
        <div class="kb-kpi-card">
          <div class="kb-kpi-label">加权平均运行时长</div>
          <div class="kb-kpi-value is-green">{{ fmtMinFromSec(summary.weightedAvgRunSeconds) }}</div>
        </div>
        <div class="kb-kpi-card">
          <div class="kb-kpi-label">运行总时长</div>
          <div class="kb-kpi-value">{{ summary.totalRunHours != null ? summary.totalRunHours : '—' }}<span style="font-size:14px"> h</span></div>
        </div>
        <div class="kb-kpi-card">
          <div class="kb-kpi-label">平均利用率</div>
          <div class="kb-kpi-value">{{ fmtPct(summary.avgUtilization) }}</div>
        </div>
        <div class="kb-kpi-card">
          <div class="kb-kpi-label">领先</div>
          <div class="kb-kpi-value" style="font-size:18px">{{ leadByMetric }}</div>
        </div>
      </div>

      <div class="kb-grid-2">
        <div class="kb-panel">
          <div class="kb-panel-head">装车台效率排名</div>
          <el-table :data="rankedBays" border stripe size="small" style="width: 100%" class="kb-rank-table">
            <el-table-column label="排名" width="72" align="center">
              <template slot-scope="{ $index }">
                <span v-if="$index === 0" class="kb-medal">🥇</span>
                <span v-else-if="$index === 1" class="kb-medal">🥈</span>
                <span v-else class="kb-rank-num">{{ $index + 1 }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="bayLabel" label="装车台" width="110" />
            <el-table-column prop="tripCount" label="车数" width="78" align="right" />
            <el-table-column label="均时长(min)" width="96" align="right">
              <template slot-scope="{ row }">{{ fmtMinNum(row.avgRunSeconds) }}</template>
            </el-table-column>
            <el-table-column label="中位(min)" width="88" align="right">
              <template slot-scope="{ row }">{{ fmtMinNum(row.medianRunSeconds) }}</template>
            </el-table-column>
            <el-table-column label="运行(h)" width="78" align="right">
              <template slot-scope="{ row }">{{ runHours(row.runSecondsTotal) }}</template>
            </el-table-column>
            <el-table-column label="利用率" width="76" align="right">
              <template slot-scope="{ row }">{{ fmtPct(row.utilization) }}</template>
            </el-table-column>
            <el-table-column prop="switchCount" label="切换" width="64" align="right" />
            <el-table-column label="综合分" width="72" align="right">
              <template slot-scope="{ row }">{{ row.score != null ? row.score : '—' }}</template>
            </el-table-column>
          </el-table>
        </div>
        <div class="kb-panel">
          <div class="kb-panel-head">两台对比 · 当前排名指标</div>
          <div ref="chartCompareRef" class="kb-chart-box kb-chart-compare" />
        </div>
      </div>

      <div class="kb-grid-2 kb-grid-2-daily">
        <div class="kb-panel">
          <div class="kb-panel-head">按日 · 装车车数</div>
          <div ref="chartTripsRef" class="kb-chart-box kb-chart-daily" />
        </div>
        <div class="kb-panel">
          <div class="kb-panel-head">按日 · 平均单均装车时长</div>
          <div ref="chartAvgRef" class="kb-chart-box kb-chart-daily" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchLoadingBayEfficiencyRank } from './api'
import { getDefaultSearchTimeRange, getSearchTimePickerOptions } from '@/basic-common/util/common'

export default {
  name: 'KanbanLoadingBayEfficiencyRank',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      loaded: false,
      dateRange: getDefaultSearchTimeRange('daterange'),
      pickerOptions: getSearchTimePickerOptions('daterange'),
      tripDetectMode: 'segment',
      minRunSec: 60,
      rankMetric: 'score',
      data: {
        bay1Found: false,
        bay2Found: false,
        summary: {},
        bays: [],
        daily: []
      },
      chartCompare: null,
      chartTrips: null,
      chartAvg: null
    }
  },
  computed: {
    summary() {
      return this.data.summary || {}
    },
    rankedBays() {
      const bays = (this.data.bays || []).map((r) => ({ ...r }))
      const m = this.rankMetric
      bays.sort((a, b) => {
        const tripA = Number(a.tripCount) || 0
        const tripB = Number(b.tripCount) || 0
        const avgA = this.toNum(a.avgRunSeconds)
        const avgB = this.toNum(b.avgRunSeconds)
        const utilA = this.toNum(a.utilization)
        const utilB = this.toNum(b.utilization)
        const scoreA = this.toNum(a.score)
        const scoreB = this.toNum(b.score)
        if (m === 'trips') return tripB - tripA
        if (m === 'avgMin') return avgA - avgB
        if (m === 'util') return utilB - utilA
        return scoreB - scoreA
      })
      return bays
    },
    leadByMetric() {
      const r = this.rankedBays[0]
      return r && r.bayLabel ? r.bayLabel : '—'
    }
  },
  watch: {
    rankedBays: {
      handler() {
        this.$nextTick(() => this.renderCompareChart())
      },
      deep: true
    }
  },
  mounted() {
    this.dateRange = getDefaultSearchTimeRange('daterange')
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    onRankMetricChange() {
      this.$nextTick(() => this.renderCompareChart())
    },
    disposeCharts() {
      [this.chartCompare, this.chartTrips, this.chartAvg].forEach((c) => {
        if (c) {
          c.dispose()
        }
      })
      this.chartCompare = null
      this.chartTrips = null
      this.chartAvg = null
    },
    toNum(v) {
      if (v == null) return 0
      const n = typeof v === 'number' ? v : parseFloat(v)
      return Number.isNaN(n) ? 0 : n
    },
    formatDateStr(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    dayEndMs(dayStr) {
      const [y, m, d] = dayStr.split('-').map((x) => parseInt(x, 10))
      const dt = new Date(y, m - 1, d, 23, 59, 59, 999)
      return dt.getTime()
    },
    dayStartMs(dayStr) {
      const [y, m, d] = dayStr.split('-').map((x) => parseInt(x, 10))
      return new Date(y, m - 1, d, 0, 0, 0, 0).getTime()
    },
    fmtInt(v) {
      if (v == null) return '—'
      return String(v)
    },
    fmtMinNum(secBd) {
      if (secBd == null || secBd === '') return '—'
      const n = this.toNum(secBd)
      if (Number.isNaN(n)) return '—'
      return (n / 60).toFixed(1)
    },
    runHours(sec) {
      if (sec == null) return '—'
      const s = Number(sec)
      if (Number.isNaN(s)) return '—'
      return (s / 3600).toFixed(1)
    },
    fmtMinFromSec(secBd) {
      if (secBd == null) return '—'
      const n = typeof secBd === 'number' ? secBd : parseFloat(secBd)
      if (Number.isNaN(n)) return '—'
      return `${(n / 60).toFixed(1)} 分`
    },
    fmtPct(bd) {
      if (bd == null) return '—'
      const n = typeof bd === 'number' ? bd : parseFloat(bd)
      if (Number.isNaN(n)) return '—'
      return `${(n * 100).toFixed(1)}%`
    },
    compareBarValue(row) {
      const m = this.rankMetric
      if (m === 'trips') return Number(row.tripCount) || 0
      if (m === 'avgMin') return Math.round((this.toNum(row.avgRunSeconds) / 60) * 10) / 10
      if (m === 'util') return Math.round(this.toNum(row.utilization) * 1000) / 10
      return Math.round(this.toNum(row.score) * 10) / 10
    },
    compareYAxisName() {
      const m = this.rankMetric
      if (m === 'trips') return '车数'
      if (m === 'avgMin') return '分钟'
      if (m === 'util') return '利用率 %'
      return '分'
    },
    shortDay(dayStr) {
      if (!dayStr || typeof dayStr !== 'string') return dayStr
      const parts = dayStr.split('-')
      return parts.length >= 3 ? `${parts[1]}-${parts[2]}` : dayStr
    },
    async loadData() {
      this.loading = true
      try {
        const params = {
          tripDetectMode: this.tripDetectMode,
          minRunDurationSec: this.minRunSec
        }
        if (this.dateRange && this.dateRange.length === 2) {
          params.startTime = this.dayStartMs(this.dateRange[0])
          params.endTime = this.dayEndMs(this.dateRange[1]) + 1
        }
        const res = await fetchLoadingBayEfficiencyRank(params)
        this.data = res || this.data
        this.loaded = true
        this.$nextTick(() => {
          this.renderCompareChart()
          this.renderDailyTripsChart()
          this.renderDailyAvgChart()
        })
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    onResize() {
      [this.chartCompare, this.chartTrips, this.chartAvg].forEach((c) => c && c.resize())
    },
    renderCompareChart() {
      const el = this.$refs.chartCompareRef
      if (!el) return
      const rows = this.rankedBays
      if (!this.chartCompare) {
        this.chartCompare = echarts.init(el)
      }
      if (!rows.length) {
        this.chartCompare.setOption({
          title: {
            text: '暂无分机台数据',
            left: 'center',
            top: 'center',
            textStyle: { color: '#94a3b8', fontSize: 14, fontWeight: 400 }
          },
          xAxis: { type: 'category', data: [] },
          yAxis: { type: 'value' },
          series: []
        })
        return
      }
      const names = rows.map((r, i) => r.bayLabel || `装车台${i + 1}`)
      const metric = this.rankMetric
      const vals = rows.map((r) => this.compareBarValue(r))
      const yName = this.compareYAxisName()
      const isUtil = metric === 'util'
      this.chartCompare.setOption({
        tooltip: {
          trigger: 'axis',
          formatter(ps) {
            const p = ps[0]
            if (!p) return ''
            return `${p.name}<br/>${yName}：<b>${p.value}${isUtil ? '%' : ''}</b>`
          }
        },
        grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
        xAxis: { type: 'category', data: names, axisLabel: { color: '#64748b' } },
        yAxis: {
          type: 'value',
          name: yName,
          splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } }
        },
        series: [
          {
            type: 'bar',
            data: vals.map((v, i) => ({
              value: v,
              itemStyle: {
                color:
                  i === 0
                    ? new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: '#f97316' },
                        { offset: 1, color: '#ea580c' }
                      ])
                    : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: '#94a3b8' },
                        { offset: 1, color: '#64748b' }
                      ]),
                borderRadius: [8, 8, 0, 0]
              }
            })),
            barWidth: '42%',
            label: {
              show: true,
              position: 'top',
              fontSize: 11,
              formatter: (p) => `${p.value}${isUtil ? '%' : ''}`
            }
          }
        ]
      })
    },
    renderDailyTripsChart() {
      const el = this.$refs.chartTripsRef
      if (!el) return
      const daily = this.data.daily || []
      if (!this.chartTrips) {
        this.chartTrips = echarts.init(el)
      }
      const days = daily.map((r) => this.shortDay(r.day))
      const t1 = daily.map((r) => r.bay1Trips || 0)
      const t2 = daily.map((r) => r.bay2Trips || 0)
      this.chartTrips.setOption({
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, textStyle: { fontSize: 11 } },
        grid: { left: '3%', right: '4%', bottom: 36, top: '12%', containLabel: true },
        xAxis: { type: 'category', data: days, axisLabel: { fontSize: 10, color: '#64748b' } },
        yAxis: {
          type: 'value',
          name: '车数',
          minInterval: 1,
          splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } }
        },
        series: [
          { name: '#1 车数', type: 'bar', data: t1, itemStyle: { color: '#ea580c' } },
          { name: '#2 车数', type: 'bar', data: t2, itemStyle: { color: '#64748b' } }
        ]
      })
    },
    renderDailyAvgChart() {
      const el = this.$refs.chartAvgRef
      if (!el) return
      const daily = this.data.daily || []
      if (!this.chartAvg) {
        this.chartAvg = echarts.init(el)
      }
      const days = daily.map((r) => this.shortDay(r.day))
      const a1 = daily.map((r) => (r.bay1AvgRunMinutes != null ? parseFloat(r.bay1AvgRunMinutes) : 0))
      const a2 = daily.map((r) => (r.bay2AvgRunMinutes != null ? parseFloat(r.bay2AvgRunMinutes) : 0))
      this.chartAvg.setOption({
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, textStyle: { fontSize: 11 } },
        grid: { left: '3%', right: '4%', bottom: 36, top: '12%', containLabel: true },
        xAxis: { type: 'category', data: days, axisLabel: { fontSize: 10, color: '#64748b' } },
        yAxis: {
          type: 'value',
          name: '分钟',
          splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } }
        },
        series: [
          { name: '#1 均时长', type: 'line', smooth: true, data: a1, itemStyle: { color: '#ea580c' } },
          { name: '#2 均时长', type: 'line', smooth: true, data: a2, itemStyle: { color: '#64748b' } }
        ]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
